import { ERowStatus, ERowTypes } from "api/enums";
import { AmountCannotBeLessThanAccumulatedException } from "api/exceptions/AmountCannotBeLessThanAccumulatedException";
import { RequiredParamException } from "api/exceptions/InvalidParamException";
import { GreaterThanOrEqualException } from "api/exceptions/InvalidValueException";
import { Transaction } from "firebase/firestore";
import { decimal } from "utils/DecimalPrecision";
import ContractRowModel from "../models/ContractRowModel";
import WBSModel from "../models/WBSModel";
import ContractRowRepository from "../repositories/ContractRowRepository";
import WBSService, { maxRowsAtSnapshot } from "./WBSService";

/**
 * Service de linhas do Contrato
 * @category Contracts
 * @hideconstructor
 */

class ContractRowService extends WBSService<ContractRowModel> {
  /** Cria uma linha de contrato do tipo insumo/serviço validando todos as
   * propriedades necessárias para criação correta.
   * A linha criada é salva no banco de dados.*/
  async createResourceRow(
    contractUid: string,
    data: {
      description: string;
      structured: string;
      quantity: number;
      unit: string;
      unitCost: number;
      rowIndex: number;
      parentStage?: string;
      parentStructured?: string;
    },
    transaction?: Transaction
  ): Promise<ContractRowModel> {
    if (!contractUid) throw new RequiredParamException("contractUid");

    const row = ContractRowModel.create("", {
      ...data,
      contractUid,
      status: ERowStatus.CHANGED,
      rowType: ERowTypes.RESOURCE,
      total: decimal(data.quantity).times(data.unitCost).toNumber(),
    });
    this.validateResourceRow(row);

    const uid = await ContractRowRepository.addContractsRowToDataBase(
      row,
      transaction
    );
    return ContractRowModel.create(uid, { ...row.toSave(), contractUid });
  }

  private validateResourceRow(row: ContractRowModel) {
    if (!row.description) throw new RequiredParamException("description");
    if (!row.structured) throw new RequiredParamException("structured");
    if (!row.parentStage) throw new RequiredParamException("parentStage");

    if (!row.quantity && row.quantity !== 0)
      throw new RequiredParamException("quantity");
    if (row.quantity < 0)
      throw new GreaterThanOrEqualException(row.quantity, 0, "quantity");

    if (!row.unitCost && row.unitCost !== 0)
      throw new RequiredParamException("unitCost");
    if (row.unitCost < 0)
      throw new GreaterThanOrEqualException(row.unitCost, 0, "unitCost");

    if (!row.rowIndex && row.rowIndex !== 0)
      throw new RequiredParamException("rowIndex");
    if (row.rowIndex < 0)
      throw new GreaterThanOrEqualException(row.rowIndex, 0, "rowIndex");
  }

  /** Altera a quantidade da linha insumo/serviço e recálcula o seu total.
   * Se a quantidade for zero ou igual a quantidade acumulada, a linha é distratada.
   * Salva as alterações no banco de dados. */
  async updateResourceRowsQuantityAndCancelIfNecessary(
    row: ContractRowModel,
    quantity: number,
    transaction?: Transaction
  ) {
    if (row.isStage) throw new Error("Row can't be a stage!");
    if ((!quantity && quantity !== 0) || quantity < 0)
      throw new GreaterThanOrEqualException(quantity, 0, "quantity");

    let total = decimal(quantity).times(row.unitCost).toNumber();
    let status = ERowStatus.CHANGED;
    const qttMeasured = row.getMeasured().quantity;

    if (qttMeasured && quantity < qttMeasured) {
      throw new AmountCannotBeLessThanAccumulatedException();
    }

    // Verifica se a quantidade é aproximada da quantidade acumulada, se for distrata toda linha
    if (qttMeasured && decimal(quantity).isEqualTo(qttMeasured)) {
      quantity = qttMeasured;
      total = row.getMeasured().total;
      status = ERowStatus.CANCELED;
    }

    // Verifica se a quantidade é aproximada de 0 (zero), se for distrata toda linha
    if (decimal(quantity).isEqualTo(0)) {
      quantity = 0;
      total = 0;
      status = ERowStatus.CANCELED;
    }

    await ContractRowRepository.updateContractsRowOnDataBase(
      row.contractUid,
      row.uid,
      { status, quantity, total },
      transaction
    );

    row.status = status;
    row.quantity = quantity;
    row.total = total;
  }

  /** Altera a quantidade da linha insumo/serviço e recálcula o seu total.
   * Salva as alterações no banco de dados. */
  async updateResourceRowsQuantity(
    row: ContractRowModel,
    quantity: number,
    transaction?: Transaction
  ) {
    if (row.isStage) throw new Error("Row can't be a stage!");
    if ((!quantity && quantity !== 0) || quantity < 0)
      throw new GreaterThanOrEqualException(quantity, 0, "quantity");

    let total = decimal(quantity).times(row.unitCost).toNumber();
    let status = ERowStatus.CHANGED;
    const qttMeasured = row.getMeasured().quantity;

    if (qttMeasured && quantity < qttMeasured) {
      throw new AmountCannotBeLessThanAccumulatedException();
    }

    await ContractRowRepository.updateContractsRowOnDataBase(
      row.contractUid,
      row.uid,
      { status, quantity, total },
      transaction
    );

    row.status = status;
    row.quantity = quantity;
    row.total = total;
  }

  /** Altera apenas o total da linha e salva no banco de dados. */
  async updateRowsTotal(
    row: ContractRowModel,
    total: number,
    transaction?: Transaction
  ) {
    if (total === undefined || total === null || total < 0) {
      throw new GreaterThanOrEqualException(total, 0, "total");
    }
    const status = row.isStage ? row.status : ERowStatus.CHANGED;

    await ContractRowRepository.updateContractsRowOnDataBase(
      row.contractUid,
      row.uid,
      { status, total },
      transaction
    );

    row.status = status;
    row.total = total;
  }

  async updateRowDescription(
    row: ContractRowModel,
    updatedDescription: string
  ) {
    updatedDescription = updatedDescription.trim();
    if (!updatedDescription) throw new RequiredParamException("description");

    await ContractRowRepository.updateContractsRowOnDataBase(
      row.contractUid,
      row.uid,
      { status: ERowStatus.CHANGED, description: updatedDescription }
    );

    row.status = ERowStatus.CHANGED;
    row.description = updatedDescription;
  }

  subscribeContractsRows(
    uid: string,
    rowsList: WBSModel<ContractRowModel>,
    onCreate: (data: ContractRowModel[]) => void,
    onChange?: (data: ContractRowModel[]) => void,
    onFinish?: Function
  ) {
    const query = ContractRowRepository.getContractsRowsQuery(
      uid,
      rowsList.lastStructuredCode,
      maxRowsAtSnapshot
    );
    this.addSnapshot(rowsList, query, onCreate, onChange, onFinish);
  }

  unsubscribeRows() {
    this.removeRowsSnapshots();
  }
}

export default new ContractRowService();
